import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, ExternalLink, Loader2, MessageCircle, Send, UserCheck, X } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../ui/alert-dialog';
import { ToolButton, PrimaryButton, EmptyState } from '../dash/Toolbar';
import { ChatMessage, ChatSession, displayName } from './types';
import { groupMessages } from './conversation';
import { MessageGroupView } from './MessageGroup';
import { statusTag } from './SessionList';

interface ConversationPanelProps {
  session: ChatSession | null;
  messages: ChatMessage[];
  onBack: () => void;
  onTakeover: () => void;
  takingOver: boolean;
  onClose: () => void;
  closing: boolean;
  newMessage: string;
  onNewMessageChange: (v: string) => void;
  onSend: () => void;
  sendingMessage: boolean;
}

export const ConversationPanel: React.FC<ConversationPanelProps> = ({
  session,
  messages,
  onBack,
  onTakeover,
  takingOver,
  onClose,
  closing,
  newMessage,
  onNewMessageChange,
  onSend,
  sendingMessage,
}) => {
  const [confirmClose, setConfirmClose] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const lastMessageId = useRef<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const last = messages[messages.length - 1];
    if (last && last.id !== lastMessageId.current) {
      lastMessageId.current = last.id;
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [newMessage]);

  if (!session) {
    return (
      <div className="flex h-full min-h-0 flex-1 items-center justify-center">
        <EmptyState icon={MessageCircle} title="Pick a conversation" hint="Select a chat from the list to view messages." />
      </div>
    );
  }

  const items = groupMessages(messages);
  const joined = session.status === 'admin_joined';
  const waiting = session.status === 'waiting_for_admin';

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (newMessage.trim()) onSend();
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col">
      <div className="flex h-14 shrink-0 items-center gap-2 border-b border-[color:var(--oa-line-2)] px-3">
        <button type="button" onClick={onBack} className="rounded-md p-1.5 text-[color:var(--oa-muted)] hover:bg-[color:var(--oa-raised-2)] md:hidden" aria-label="Back to conversations">
          <ArrowLeft className="h-4 w-4" />
        </button>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="truncate text-[13.5px] font-medium text-[color:var(--oa-text)]">{displayName(session)}</p>
            {statusTag(session.status)}
          </div>
          {session.visitor_email ? <p className="truncate text-[11.5px] text-[color:var(--oa-muted)]">{session.visitor_email}</p> : null}
        </div>

        <div className="flex shrink-0 items-center gap-1.5">
          {session.current_page ? (
            <ToolButton onClick={() => window.open(session.current_page as string, '_blank', 'noopener,noreferrer')} title="View page">
              <ExternalLink className="h-4 w-4" />
              <span className="hidden sm:inline">View page</span>
            </ToolButton>
          ) : null}

          {!joined ? (
            <PrimaryButton onClick={onTakeover} disabled={takingOver}>
              {takingOver ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserCheck className="h-4 w-4" />}
              Take over
            </PrimaryButton>
          ) : null}

          <ToolButton onClick={() => setConfirmClose(true)} title="Close chat">
            <X className="h-4 w-4" />
          </ToolButton>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto bg-[color:var(--oa-raised)]">
        <div className="mx-auto flex max-w-[760px] flex-col gap-3 px-4 py-5">
          {items.map((item) => (item.kind === 'day' ? (
            <div key={item.key} className="my-1 text-center text-[11px] font-medium uppercase tracking-wide text-[color:var(--oa-faint)]">{item.label}</div>
          ) : (
            <MessageGroupView key={item.key} item={item} />
          )))}
          <div ref={bottomRef} />
        </div>
      </div>

      {joined ? (
        <div className="shrink-0 border-t border-[color:var(--oa-line-2)] p-3">
          <div className="flex items-end gap-2 rounded-xl border border-[color:var(--oa-line-2)] bg-[color:var(--oa-card)] p-2 focus-within:border-[color:var(--oa-accent)] focus-within:ring-2 focus-within:ring-[color:color-mix(in_oklab,var(--oa-accent)_20%,transparent)]">
            <textarea
              ref={textareaRef}
              rows={1}
              value={newMessage}
              onChange={(e) => onNewMessageChange(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type your message…"
              className="max-h-40 flex-1 resize-none bg-transparent px-1.5 py-1 text-[13.5px] text-[color:var(--oa-text)] outline-none placeholder:text-[color:var(--oa-faint)]"
            />
            <button
              type="button"
              onClick={onSend}
              disabled={sendingMessage || !newMessage.trim()}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[color:var(--oa-accent)] text-[color:var(--oa-on-accent)] transition-colors hover:bg-[color:var(--oa-accent-2)] disabled:opacity-50"
            >
              {sendingMessage ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </button>
          </div>
          <p className="mt-1.5 px-1 text-[11px] text-[color:var(--oa-faint)]">Enter to send · Shift+Enter for a new line</p>
        </div>
      ) : (
        <div className="flex shrink-0 items-center justify-between gap-3 border-t border-[color:var(--oa-line-2)] bg-[color:var(--oa-raised)] px-4 py-3">
          <p className="text-[12.5px] text-[color:var(--oa-muted)]">{waiting ? 'This visitor asked for a person.' : 'The AI assistant is handling this chat.'}</p>
          {waiting ? (
            <PrimaryButton onClick={onTakeover} disabled={takingOver}>
              {takingOver ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserCheck className="h-4 w-4" />}
              Take over
            </PrimaryButton>
          ) : null}
        </div>
      )}

      <AlertDialog open={confirmClose} onOpenChange={setConfirmClose}>
        <AlertDialogContent className="rounded-xl font-poppins">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-montserrat text-[16px] font-extrabold text-[color:var(--oa-text)]">Close this chat?</AlertDialogTitle>
            <AlertDialogDescription className="text-[13px] text-[color:var(--oa-muted)]">
              {displayName(session)} will no longer be able to continue this conversation.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-8 rounded-md text-[12.5px]">Keep open</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmClose(false);
                onClose();
              }}
              disabled={closing}
              className="h-8 rounded-md bg-[color:var(--oa-bad)] text-[12.5px] text-white hover:bg-[color:var(--oa-bad)]"
            >
              Close chat
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
